import { LoggerService } from "@/shared/services/LoggerService";

import type {
  ActivityPeriod,
  InvocationActivityResponse,
} from "./ActionRecord";

/** One bucket on the full grid: the server sends only the buckets in which something happened. */
export interface FilledActivityBucket {
  periodStart: Date;
  peakInStatus: Record<string, number>;
  peakConcurrency: number;
  enteredStatus: Record<string, number>;
  computeCreditMillionths: number;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** A span of time with both bounds resolved. */
export interface TimeRange {
  start: Date;
  end: Date;
}

/** The bounds of a {@link TimeWindow} as of one instant. */
export interface ResolvedWindow extends TimeRange {
  /**
   * Whether the span was cut at {@link ACTIVITY_WINDOW_MAX_MS}: activity older than `start` was
   * asked for and is not in this window.
   */
  truncated: boolean;
}

/**
 * A span of time that may follow the clock, such as "the last 24 hours".
 *
 * The identity and the bounds are kept apart so a query can be cached on the one and fetched
 * with the other. A window that slides forward a minute at a time is still the same question,
 * so a refresh of it is a refetch that keeps the previous answer on screen; only a different
 * question (a new filter, a zoom) starts over.
 */
export interface TimeWindow {
  /** Serializable, and equal for two windows that ask the same thing whatever the time is. */
  key: unknown;
  resolve: (now: Date) => ResolvedWindow;
}

/** A window whose bounds do not move; its key is the bounds. */
export function fixedWindow(range: TimeRange): TimeWindow {
  return {
    key: [range.start.toISOString(), range.end.toISOString()],
    resolve: () => ({ start: range.start, end: range.end, truncated: false }),
  };
}

/**
 * The wall clock rounded down to the minute: the same anchor a date preset expands against, so a
 * window resolved here and a "created" condition resolved by the filter bar describe one span.
 */
export function currentMinute(): Date {
  return new Date(Math.floor(Date.now() / MINUTE) * MINUTE);
}

/**
 * The widest window the activity API accepts. Mirrors `MAX_ACTIVITY_WINDOW` in
 * `roboto_service/domain/actions/invocation_activity.py`, which is what enforces it.
 */
export const ACTIVITY_WINDOW_MAX_MS = 5 * 366 * DAY;
/** {@link ACTIVITY_WINDOW_MAX_MS} as a reader would say it. */
export const ACTIVITY_WINDOW_MAX_LABEL = "5 years";

/** Finest first. Every width nests in the next, so "coarser" is simply "later in this list". */
export const ACTIVITY_PERIODS: readonly ActivityPeriod[] = [
  "1m",
  "5m",
  "15m",
  "1h",
  "6h",
  "1d",
  "1w",
  "1mo",
];

/**
 * The finest bucket the server allows for a window of this length; wider windows get months.
 * Mirrors `PERIOD_LADDER` in `roboto_service/domain/actions/invocation_activity.py`, which is
 * what actually enforces it.
 */
export function minimumPeriodFor(windowMs: number): ActivityPeriod {
  if (windowMs <= 3 * HOUR) {
    return "1m";
  }
  if (windowMs <= 24 * HOUR) {
    return "5m";
  }
  if (windowMs <= 7 * DAY) {
    return "1h";
  }
  if (windowMs <= 30 * DAY) {
    return "6h";
  }
  if (windowMs <= 90 * DAY) {
    return "1d";
  }
  if (windowMs <= 366 * DAY) {
    return "1w";
  }
  return "1mo";
}

/** How wide a month is taken to be when asking whether it fits inside a window. */
const SHORTEST_MONTH = 28 * DAY;

export interface PeriodOption {
  period: ActivityPeriod;
  /** False when the width is finer than the window's minimum or wider than the window itself. */
  allowed: boolean;
}

/**
 * Every period, in order, marked with whether this window may be drawn at it: nothing finer than
 * the ladder allows, and nothing wider than the window itself.
 *
 * A width equal to the window stays on offer, and a month counts as fitting any window of at least
 * {@link SHORTEST_MONTH} however long that calendar month turns out to be. So the coarsest allowed
 * option can yield a single bucket.
 */
export function periodOptionsFor(windowMs: number): PeriodOption[] {
  const finest = ACTIVITY_PERIODS.indexOf(minimumPeriodFor(windowMs));
  return ACTIVITY_PERIODS.map((period, index) => ({
    period,
    allowed:
      index >= finest &&
      (periodMilliseconds(period) ?? SHORTEST_MONTH) <= windowMs,
  }));
}

/** Width of one bucket. A month has no fixed width; see {@link nextBucketStart}. */
export function periodMilliseconds(period: ActivityPeriod): number | null {
  switch (period) {
    case "1m":
      return MINUTE;
    case "5m":
      return 5 * MINUTE;
    case "15m":
      return 15 * MINUTE;
    case "1h":
      return 60 * MINUTE;
    case "6h":
      return 360 * MINUTE;
    case "1d":
      return 1_440 * MINUTE;
    case "1w":
      return 7 * 1_440 * MINUTE;
    case "1mo":
      return null;
  }
}

/** The start of the bucket after the one starting at `start`. */
export function nextBucketStart(start: Date, period: ActivityPeriod): Date {
  const width = periodMilliseconds(period);
  if (width !== null) {
    return new Date(start.getTime() + width);
  }
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
}

/**
 * Every bucket from the response's start to its end, in order, with the empty ones at zero.
 *
 * A chart drawn from the sparse list would join the last busy bucket to the next one with a line,
 * hiding the quiet period between them.
 *
 * Buckets are matched on the exact millisecond, so a server bucket whose start is off the grid
 * this steps from `start_time` has nowhere to land. The ladder is mirrored by hand in
 * `roboto_service/domain/actions/invocation_activity.py`; were it ever to drift, dropping those
 * buckets silently would draw as zeros and read as an idle org, so the dropped count is logged.
 */
export function fillActivityBuckets(
  response: InvocationActivityResponse,
): FilledActivityBucket[] {
  const unmatched = new Map(
    response.buckets.map((bucket) => [
      new Date(bucket.period_start).getTime(),
      bucket,
    ]),
  );
  const end = new Date(response.end_time).getTime();
  const filled: FilledActivityBucket[] = [];

  for (
    let start = new Date(response.start_time);
    start.getTime() < end;
    start = nextBucketStart(start, response.period)
  ) {
    const bucket = unmatched.get(start.getTime());
    unmatched.delete(start.getTime());
    filled.push({
      periodStart: start,
      peakInStatus: bucket?.peak_in_status ?? {},
      peakConcurrency: bucket?.peak_concurrency ?? 0,
      enteredStatus: bucket?.entered_status ?? {},
      computeCreditMillionths: bucket?.compute_credit_millionths ?? 0,
    });
  }

  if (unmatched.size > 0) {
    LoggerService.warn(
      `Dropped ${unmatched.size} of ${response.buckets.length} "${response.period}" activity ` +
        `buckets whose starts are off the grid stepped from ${response.start_time}; the chart ` +
        `draws zero where they belong.`,
    );
  }

  return filled;
}

/**
 * An axis label for a bucket, carrying as much of the date as the period makes necessary: time of
 * day repeats across a multi-day window, a date repeats across a multi-month one.
 *
 * `withDate` adds the date to a minute-scale label; pass it when the buckets cross midnight, where
 * "12:40 PM" alone could be either day.
 *
 * A bucket an hour or finer is labelled in the reader's zone, because the label names an instant
 * and the reader compares it against the local timestamps elsewhere on the page. A day, week or
 * month bucket is labelled in UTC, because the label names a calendar square rather than an
 * instant, and the server cuts those squares with `date_bin` against a UTC origin: every such
 * `period_start` is a UTC midnight, which west of UTC falls on the previous local day. Rendered
 * locally, the bucket covering 2026-03-19 UTC would read "Mar 18".
 */
export function formatBucketStart(
  start: Date,
  period: ActivityPeriod,
  options: { withDate?: boolean; locale?: string } = {},
): string {
  const { withDate = false, locale } = options;
  switch (period) {
    case "1m":
    case "5m":
    case "15m":
      return withDate
        ? start.toLocaleString(locale, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          })
        : start.toLocaleTimeString(locale, {
            hour: "numeric",
            minute: "2-digit",
          });
    case "1h":
    case "6h":
      return start.toLocaleString(locale, {
        month: "short",
        day: "numeric",
        hour: "numeric",
      });
    case "1d":
    case "1w":
      return start.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
    case "1mo":
      return start.toLocaleDateString(locale, {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
  }
}

/** Whether the buckets' starts fall on more than one calendar day, in the reader's time zone. */
export function spansMultipleDays(buckets: { periodStart: Date }[]): boolean {
  if (buckets.length < 2) {
    return false;
  }
  const first = buckets[0].periodStart;
  const last = buckets[buckets.length - 1].periodStart;
  return first.toDateString() !== last.toDateString();
}
